import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Flame, Shield, TrendingUp, Award, ArrowRight, CheckCircle, Thermometer, Droplets, Zap, Download, Calculator, X, HelpCircle, ExternalLink, FileText, Beaker } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import UnifiedContactModal from '../components/UnifiedContactModal';
import NewsletterPopup from '../components/NewsletterPopup';
import CropGrid from '../components/CropGrid';
import AcidCostCalculator from '../components/AcidCostCalculator';
import SulfurBurnerTwoCTA from '../components/SulfurBurnerTwoCTA';

const SulfurBurnersPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Consistent Water Chemistry',
      description: 'Many growers report yield and quality improvements when pH and scaling risks are controlled through inline water management'
    },
    {
      icon: Shield,
      title: 'Reduce System Clogging',
      description: 'Neutralize bicarbonates to prevent scaling in emitters and lines, keeping your irrigation system running predictably'
    },
    {
      icon: Award,
      title: 'Superior Quality',
      description: 'Built with premium acid-resistant materials for long-lasting performance in harsh agricultural conditions'
    }
  ];

  const comparisons = [
    {
      feature: 'Materials',
      ctc: 'Advanced acid-resistant alloys',
      others: 'Standard materials'
    },
    {
      feature: 'Build Quality',
      ctc: 'Precision manufacturing',
      others: 'Outdated'
    },
    {
      feature: 'Automation',
      ctc: 'Superior automated controls',
      others: 'Complicated & Unreliable'
    },
    {
      feature: 'Components',
      ctc: 'Premium grade parts',
      others: 'Basic components'
    },
    {
      feature: 'Support',
      ctc: 'Dedicated Expert Service',
      others: 'Limited support'
    }
  ];

  const faqData = [
    {
      question: "Salinity vs sodicity vs SAR — what's the difference?",
      answer: "Salinity refers to total dissolved salts in water or soil (measured as EC or TDS). Sodicity is the proportion of sodium relative to calcium and magnesium on soil exchange sites. SAR (Sodium Adsorption Ratio) is the calculation that predicts sodium's impact on soil structure. High salinity hurts osmotic balance; high sodicity breaks down soil structure and reduces infiltration."
    },
    {
      question: "Does a sulfur burner 'make sulfuric acid'?",
      answer: "Not directly. A sulfur burner combusts elemental sulfur with air to produce sulfur dioxide (SO₂). When SO₂ dissolves in water, it forms sulfurous acid (H₂SO₃), which can further oxidize to sulfuric acid (H₂SO₄) over time. The key point: you're generating acid inline from a safe, solid feedstock rather than handling concentrated liquid acid."
    },
    {
      question: "What goals can treated irrigation support?",
      answer: "Treated irrigation can support several goals: hitting a target pH, neutralizing bicarbonates, reducing scaling/clogging risk, and (in the right conditions) supporting salinity/sodicity programs. pH goals are application-specific. We start with your goal and constraints, then set realistic expectations. If you have additional water/soil records, we can tighten assumptions and tailor operating targets."
    },
    {
      question: "How long does it take to see results?",
      answer: "Water pH adjusts immediately. Soil pH changes can be observed within weeks to months depending on irrigation frequency, soil buffering capacity, and depth of water penetration. Crop response (reduced chlorosis, improved vigor) may appear within one growing season. Multi-year data is needed to confirm sustained salinity or sodicity improvements."
    },
    {
      question: "What information helps us give you tighter expectations?",
      answer: "Tier 1 (Most common / enough to start): Target pH goal, Flow (GPM), Irrigation method (drip/sprinkler/flood). Tier 2 (More confident plan for bicarbonates + scaling): Water alkalinity/bicarbonate (HCO3-), Hardness (Ca/Mg), Basic sodium (if available). Tier 3 (Salinity/sodicity context-dependent): Soil EC / salinity by depth (if available), SAR / sodium indicators (if available), Notes on drainage + whether leaching is realistic. Not everyone needs every test—start simple; more data increases confidence."
    },
    {
      question: "Can this replace gypsum or soil amendments?",
      answer: "No. Treated irrigation addresses water chemistry and can support soil reclamation, but it doesn't replace calcium amendments like gypsum when you need to physically displace sodium from soil. Think of acidified irrigation as a complementary tool, not a substitute for a complete salinity/sodicity management program."
    },
    {
      question: "Is it safe for drip systems and materials?",
      answer: "When operated correctly, sulfur burner systems produce mildly acidic water that is compatible with modern drip irrigation materials (PVC, polyethylene, drip tape). Avoid over-acidification, which can corrode metal fittings. We recommend pH monitoring at injection point and typical operating ranges to protect equipment while achieving agronomic goals."
    },
    {
      question: "What's the biggest reason these projects fail?",
      answer: "Mismatch between expectations and site constraints. If drainage is poor, soil is already compacted, or water chemistry doesn't warrant treatment, acidified irrigation won't solve the problem. Success requires honest assessment of water quality, soil conditions, and realistic operating parameters before installation."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Sulfur Burners | Agricultural pH Management Systems"
        description="Decades of proof. The most efficient and reliable pH management systems for agricultural water treatment. Documented outcomes can include improved yield and quality when chemistry and field constraints fit."
        canonicalPath="/sulfur-burners"
      />
      <Navbar />

      {/* FAQ Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Flame size={16} />
              <span>Industries Most Durable</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Sulfur Burners
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              Inline irrigation water chemistry management (pH + bicarbonates) for predictable operation and cleaner systems.
            </p>
            <p className="text-base text-gray-500 italic max-w-2xl mx-auto mb-8">
              Built on decades of field experience. Used by growers and water managers across a wide range of crops and regions.
            </p>

            <div className="max-w-5xl mx-auto">
              <SulfurBurnerTwoCTA layout="stacked" size="large" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">

          {/* What Are Sulfur Burners Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                What Are Sulfur Burners?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Understanding the technology that's been improving agricultural water quality for decades
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">How They Work</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Sulfur burners are inline water treatment systems that burn elemental sulfur to produce sulfur dioxide (SO₂).
                    When this SO₂ dissolves in your irrigation water, it forms sulfurous acid, which naturally lowers the pH
                    and neutralizes bicarbonates that can harm your crops and clog your irrigation systems.
                  </p>

                  <div className="space-y-4">
                    {[
                      'Burns elemental sulfur at controlled temperatures',
                      'Produces sulfur dioxide gas automatically',
                      'Dissolves SO₂ into irrigation water inline',
                      'Creates sulfurous acid to lower pH naturally',
                      'Neutralizes harmful bicarbonates effectively'
                    ].map((step, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                          {index + 1}
                        </div>
                        <span className="text-gray-700">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Benefits</h4>
                  <div className="space-y-3">
                    {[
                      'Safer than handling liquid acids',
                      'Provides sulfur nutrition to crops',
                      'Automated operation with minimal maintenance',
                      'Prevents irrigation system clogging',
                      'Improves nutrient availability'
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Outcome Tiles - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                What Treated Irrigation Helps You Accomplish
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Same tool. Different objectives. Start simple—and get more precise when you have more data.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                  <Droplets className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Hit a Target pH</h3>
                <p className="text-gray-700 text-sm">Fertigation goals, crop-specific pH targets, and controlled nutrient delivery.</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Neutralize Alkalinity / Bicarbonates</h3>
                <p className="text-gray-700 text-sm">Stability, nutrient availability, and prevention of bicarbonate-induced issues.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Reduce Scaling / Clogging Risk</h3>
                <p className="text-gray-700 text-sm">System protection, uptime, and reduced maintenance on irrigation equipment.</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Support Salinity / Sodicity Programs</h3>
                <p className="text-gray-700 text-sm mb-3">Advanced use case; constraint-dependent.</p>
                <p className="text-gray-600 text-xs italic">In the right conditions, treated irrigation can support soil health goals by improving root-zone chemistry stability and helping salts move where leaching is realistic.</p>
              </div>
            </div>
          </motion.div>

          {/* Research Highlights (University + Field) - NEW GROUPED SECTION */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Research Highlights (University + Field)
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                University research demonstrates how treated irrigation performs in specific water chemistry, soil, and crop contexts.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Texas A&M Card */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-300 shadow-lg">
                <div className="flex items-start space-x-3 mb-6">
                  <FileText className="text-blue-600 flex-shrink-0 mt-1" size={28} />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Texas A&M AgriLife
                    </h3>
                    <p className="text-blue-700 font-medium">Far West Texas Cotton</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  In calcareous soil regions with salinity and sodicity challenges, acidified irrigation water was tested as part of a comprehensive management program that included realistic leaching and drainage planning.
                </p>

                <div className="bg-white rounded-lg p-5 mb-6 shadow-md">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Findings</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Salinity reduction in upper layer after one season</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Modest SAR shifts by depth—distribution matters</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">~20% yield vs long-term average + fiber quality</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">In the right conditions, improving infiltration/permeability and root-zone chemistry can support soil health goals</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 mb-6">
                  <p className="text-gray-800 font-medium text-sm">
                    Early results are encouraging—but performance depends on site conditions (soil calcium, drainage, and realistic leaching). Treated irrigation is a chemistry lever inside a broader salinity/sodicity plan—not a standalone cure.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <Link
                    to="/blog/texas-a-m-agrilife-cotton-study-sulfur-burner-treated-irrigation-reduced-root-zone-salinity-and-increased-yield-20"
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-5 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-md text-center"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Read CTC breakdown</span>
                      <ArrowRight size={16} />
                    </div>
                  </Link>
                  <a
                    href="https://elpaso.tamu.edu/blog/2024/07/12/evaluation-of-sulfur-burner-for-salinity-management-in-irrigated-cotton-fields-in-far-west-texas/?utm_source=chatgpt.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border-2 border-blue-600 text-blue-600 px-5 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300 text-center"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Texas A&M source</span>
                      <ExternalLink size={16} />
                    </div>
                  </a>
                </div>
              </div>

              {/* OSU Card */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-300 shadow-lg">
                <div className="flex items-start space-x-3 mb-6">
                  <Beaker className="text-green-600 flex-shrink-0 mt-1" size={28} />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Oregon State University
                    </h3>
                    <p className="text-green-700 font-medium">Hermiston Organic Blueberries</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Researchers established an organic northern highbush blueberry planting on sandy, calcareous soil with high-pH irrigation water to study pH management and organic input strategies.
                </p>

                <div className="bg-white rounded-lg p-5 mb-6 shadow-md">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">What It Means</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Pre-acidified soil + sulfur burner acidified irrigation</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Maintained blueberry pH sweet spot in high-pH environment</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Tested alongside compost and soil health strategies</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-100 border-l-4 border-green-500 rounded-lg p-4 mb-6">
                  <p className="text-gray-800 font-medium text-sm">
                    Real-world context for pH-sensitive, high-value crops facing alkaline conditions.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <Link
                    to="/blog/blueberries-high-ph-water-and-osu-s-organic-trial-what-acidified-irrigation-really-buys-you"
                    className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-5 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-md text-center"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Read CTC article</span>
                      <ArrowRight size={16} />
                    </div>
                  </Link>
                  <a
                    href="https://agsci.oregonstate.edu/harec/article/optimizing-soil-ph-organic-blueberries-balancing-act?utm_source=chatgpt.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border-2 border-green-600 text-green-600 px-5 py-3 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-all duration-300 text-center"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>OSU Extension source</span>
                      <ExternalLink size={16} />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Sulfur Burners Size & Flow Overview Table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Sulfur Burners — Size & Flow Overview</h4>
            <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
              <table className="w-full border-collapse" aria-label="Sulfur Burners size and flow overview">
                <thead className="bg-gradient-to-r from-orange-600 to-red-600 text-white sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Size</th>
                    <th className="px-4 py-3 text-left font-semibold">Flow Rates (GPM)</th>
                    <th className="px-4 py-3 text-left font-semibold">Burn Rate (lbs/hr)</th>
                    <th className="px-4 py-3 text-left font-semibold">Approx. Weight (lbs)</th>
                    <th className="px-4 py-3 text-left font-semibold">Item #</th>
                    <th className="px-4 py-3 text-center font-semibold">Drawing (PDF)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-orange-50">
                    <td className="px-4 py-3 font-semibold">MINI</td>
                    <td className="px-4 py-3">50–600</td>
                    <td className="px-4 py-3">1–5</td>
                    <td className="px-4 py-3">200</td>
                    <td className="px-4 py-3">1-000080</td>
                    <td className="px-4 py-3 text-center">
                      <a
                        href="/download/Mini Sulfur Burner (1-000080) - Sales Drawing.pdf"
                        download
                        className="inline-flex items-center justify-center text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                        aria-label="Download Mini Sulfur Burner drawing"
                      >
                        <Download size={20} />
                      </a>
                    </td>
                  </tr>
                  <tr className="hover:bg-orange-50">
                    <td className="px-4 py-3 font-semibold">SMALL</td>
                    <td className="px-4 py-3">400–1200</td>
                    <td className="px-4 py-3">3–6</td>
                    <td className="px-4 py-3">300</td>
                    <td className="px-4 py-3">1-000019</td>
                    <td className="px-4 py-3 text-center">
                      <a
                        href="/download/Small Sulfur Burner (1-000019) - Sales Drawing.pdf"
                        download
                        className="inline-flex items-center justify-center text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                        aria-label="Download Small Sulfur Burner drawing"
                      >
                        <Download size={20} />
                      </a>
                    </td>
                  </tr>
                  <tr className="hover:bg-orange-50">
                    <td className="px-4 py-3 font-semibold">MEDIUM</td>
                    <td className="px-4 py-3">800–2000</td>
                    <td className="px-4 py-3">6–10</td>
                    <td className="px-4 py-3">500</td>
                    <td className="px-4 py-3">1-000060</td>
                    <td className="px-4 py-3 text-center">
                      <a
                        href="/download/Medium Sulfur Burner (1-000060) - Sales Drawing.pdf"
                        download
                        className="inline-flex items-center justify-center text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                        aria-label="Download Medium Sulfur Burner drawing"
                      >
                        <Download size={20} />
                      </a>
                    </td>
                  </tr>
                  <tr className="hover:bg-orange-50">
                    <td className="px-4 py-3 font-semibold">LARGE</td>
                    <td className="px-4 py-3">1400–3000</td>
                    <td className="px-4 py-3">8–14</td>
                    <td className="px-4 py-3">650</td>
                    <td className="px-4 py-3">1-000067</td>
                    <td className="px-4 py-3 text-center">
                      <a
                        href="/download/Large Sulfur Burner (1-000067) - Sales Drawing.pdf"
                        download
                        className="inline-flex items-center justify-center text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                        aria-label="Download Large Sulfur Burner drawing"
                      >
                        <Download size={20} />
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Why pH Matters Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Why pH Matters for Your Crops
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The hidden factor that can make or break your harvest
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-xl font-bold text-red-800 mb-4">High pH Problems</h3>
                <div className="space-y-3">
                  {[
                    'Nutrients become "locked up" and unavailable',
                    'Iron, zinc, and manganese deficiencies',
                    'Stunted growth and poor yields',
                    'Increased disease susceptibility',
                    'Clogged drip emitters and sprinklers',
                    'Wasted fertilizer investments'
                  ].map((problem, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <span className="text-red-600 font-bold mt-1">•</span>
                      <span className="text-red-700">{problem}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-xl font-bold text-green-800 mb-4">Optimal pH Benefits</h3>
                <div className="space-y-3">
                  {[
                    'Maximum nutrient availability and uptake',
                    'Healthy, vigorous plant growth',
                    'Higher yields and better quality',
                    'Improved disease resistance',
                    'Clean, efficient irrigation systems',
                    'Better return on fertilizer investment'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-green-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* The Bicarbonate Problem Section - COMPACT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                The Hidden Bicarbonate Problem
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Think of them as tiny pH "elevators" that gradually raise your soil pH over time, even if your water looks clean and clear.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Interpretation Tiers - Compact */}
                <div className="lg:col-span-3">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">The 120 PPM Rule</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="font-semibold text-green-800 mb-1 text-sm">&lt; 120 ppm</p>
                      <p className="text-gray-700 text-xs">Typically manageable</p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="font-semibold text-yellow-800 mb-1 text-sm">120–150 ppm</p>
                      <p className="text-gray-700 text-xs">Issues often begin</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="font-semibold text-red-800 mb-1 text-sm">&gt; 150 ppm</p>
                      <p className="text-gray-700 text-xs">Mitigation merited</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs mt-3 italic">
                    Final recommendation requires full water report and system constraints.
                  </p>
                </div>

                {/* Common Sources - Compact Chips */}
                <div className="lg:col-span-2">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Common Sources</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'Deep wells',
                      'Limestone regions',
                      'Recycled water',
                      'Municipal supply',
                      'Canal water'
                    ].map((source, index) => (
                      <span key={index} className="inline-flex items-center space-x-1 bg-white px-3 py-1.5 rounded-full text-sm text-gray-700 border border-blue-200">
                        <Droplets size={14} className="text-blue-500" />
                        <span>{source}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>


          {/* Self-Qualify CTA - Two Lane Options */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border-2 border-cyan-300 shadow-lg">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
                <p className="text-lg text-gray-700">
                  We can size and quote off pH + peak GPM. A full water report helps tighten expectations and commissioning targets.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploads Optional—Start with What You Have:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">✓</div>
                    <div className="text-gray-700">
                      <strong>Required:</strong> Irrigation water pH + Peak flow (GPM)
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-400 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5">+</div>
                    <div className="text-gray-700">
                      <strong>Helpful:</strong> Water alkalinity/bicarbonates (if you have it)
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-400 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5">+</div>
                    <div className="text-gray-700">
                      <strong>Helpful:</strong> Irrigation method (drip/sprinkler/flood)
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-400 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5">+</div>
                    <div className="text-gray-700">
                      <strong>Optional:</strong> Full water + soil reports for detailed analysis
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="max-w-4xl mx-auto">
                  <SulfurBurnerTwoCTA layout="horizontal" size="medium" showPositioning={false} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* System Clogging Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Protecting Your Irrigation Investment
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                How sulfur burners prevent costly system damage and downtime
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-xl font-bold text-red-800 mb-4">Without pH Management</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Drip Systems</h4>
                    <p className="text-gray-700 text-sm">Calcium and magnesium precipitates clog emitters, causing uneven water distribution and crop stress.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Sprinkler Systems</h4>
                    <p className="text-gray-700 text-sm">Scale buildup in nozzles reduces flow rates and creates uneven coverage patterns.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Maintenance Costs</h4>
                    <p className="text-gray-700 text-sm">Frequent cleaning, replacement parts, and system downtime during critical growing periods.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-xl font-bold text-green-800 mb-4">With CTC Sulfur Burners</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Clean Systems</h4>
                    <p className="text-gray-700 text-sm">Acidified water dissolves scale and prevents new buildup, keeping emitters and nozzles flowing freely.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Uniform Application</h4>
                    <p className="text-gray-700 text-sm">Consistent water and nutrient delivery across your entire field for optimal crop performance.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Reduced Maintenance</h4>
                    <p className="text-gray-700 text-sm">Fewer clogs mean less downtime, lower maintenance costs, and more reliable irrigation.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* When & Where to Use One Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                When &amp; Where to Use One
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Perfect for These Crops
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
              {/* Crop Grid */}
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2 md:gap-3 mb-8">
                {[
                  // Row 1
                  { name: 'Almonds', emoji: '/Almond.png', ph: '6.0-7.0', link: '/almond', benefits: ['Unlock micronutrients in high-pH soils', 'Stronger nut fill and development', 'Reduced iron chlorosis symptoms'] },
                  { name: 'Apples', emoji: '🍎', ph: '6.0-6.5', link: '/apple', benefits: ['Improve calcium uptake for crisp texture', 'Better fruit finish quality', 'Longer shelf life'] },
                  { name: 'Apricots', emoji: '🍑', ph: '6.0-7.0', link: '/apricot', benefits: ['Lower bicarbonate buildup', 'Reduce fruit cracking', 'Better color development'] },
                  { name: 'Artichokes', emoji: '/Artichoke.png', ph: '6.5-7.5', link: '/artichoke', benefits: ['Balance soil pH for bud quality', 'Improve uniformity', 'Better nutrient uptake'] },
                  { name: 'Asparagus', emoji: '/Asparagus.png', ph: '6.0-8.0', link: '/asparagus', benefits: ['Support deep root growth', 'Improve spear quality', 'Better soil structure'] },
                  { name: 'Avocados', emoji: '🥑', ph: '6.0-7.0', link: '/avocado', benefits: ['Prevent iron chlorosis', 'Reduce leaf drop', 'Better fruit set'] },
                  { name: 'Beans', emoji: '🫘', ph: '6.0-7.0', link: '/bean', benefits: ['Maximize nitrogen-fixing efficiency', 'Better pod development', 'Improved yield'] },
                  { name: 'Beets', emoji: '/Beets Emoji.png', ph: '6.0-7.5', link: '/beet', benefits: ['Promote better color and sugar content', 'Improved root development', 'Better storage quality'] },
                  { name: 'Blackberries', emoji: '/Blackberry Emoji.png', ph: '5.0-6.0', link: '/blackberry', benefits: ['Improve flavor and size', 'Enhanced nutrient mobility', 'Better cane health'] },
                  { name: 'Blueberries', emoji: '🫐', ph: '4.5-5.5', link: '/blueberry', benefits: ['Low pH = premium yield', 'Deep color and sweeter berries', 'Prevent iron chlorosis'] },

                  // Row 2
                  { name: 'Broccoli', emoji: '🥦', ph: '6.0-7.0', link: '/broccoli', benefits: ['Avoid boron lockout', 'Tight, healthy heads', 'Better color retention'] },
                  { name: 'Brussels Sprouts', emoji: '/Brussels Sprout.png', ph: '6.0-7.5', link: '/brussels-sprouts', benefits: ['Boost sulfur metabolism', 'Flavor and firmness', 'Better head formation'] },
                  { name: 'Cabbage', emoji: '/Cabbage.png', ph: '6.0-7.5', link: '/cabbage', benefits: ['Reduce tip burn', 'Improve wrapper leaf development', 'Better head density'] },
                  { name: 'Cannabis', emoji: '/Cannibis Emoji.png', ph: '5.8-6.5', link: '/cannabis', benefits: ['Lower pH for max terpene expression', 'Better nutrient uptake', 'Improved resin production'] },
                  { name: 'Carrots', emoji: '🥕', ph: '5.5-7.0', link: '/carrot', benefits: ['Enhance phosphorus access', 'Longer, smoother roots', 'Better color development'] },
                  { name: 'Cauliflower', emoji: '/Cauliflower Emoji.png', ph: '5.5-7.5', link: '/cauliflower', benefits: ['Combat bicarbonate stress', 'Ensure tight, white curds', 'Better head formation'] },
                  { name: 'Celery', emoji: '/Celery Emoji.png', ph: '5.8-7.0', link: '/celery', benefits: ['Prevent tip burn and hollow stems', 'Improved Ca uptake', 'Better stalk quality'] },
                  { name: 'Cherries', emoji: '🍒', ph: '6.0-7.0', link: '/cherry', benefits: ['Reduce fruit splitting', 'Balance micronutrient availability', 'Better stem quality'] },
                  { name: 'Citrus', emoji: '🍊', ph: '6.0-7.5', link: '/citrus', benefits: ['Unlock iron and zinc', 'Brighten foliage and flavor', 'Prevent chlorosis'] },
                  { name: 'Corn', emoji: '🌽', ph: '6.0-6.8', link: '/corn', benefits: ['Lower bicarbonates for early vigor', 'Enhance stand uniformity', 'Better nutrient uptake'] },

                  // Row 3
                  { name: 'Cranberries', emoji: '/Cranberries.png', ph: '4.0-5.5', link: '/cranberry', benefits: ['Maintain ultra-low pH', 'Root health and fruit set', 'Disease resistance'] },
                  { name: 'Cucumbers', emoji: '🥒', ph: '5.5-7.0', link: '/cucumber', benefits: ['Keep soil balanced', 'Avoid bitter fruit', 'Prevent blossom end rot'] },
                  { name: 'Eggplant', emoji: '🍆', ph: '5.5-6.5', link: '/eggplant', benefits: ['Boost potassium availability', 'Firmer, glossier fruit', 'Better color development'] },
                  { name: 'Garlic', emoji: '🧄', ph: '6.0-7.0', link: '/garlic', benefits: ['Support healthy bulb formation', 'Stabilize pH at roots', 'Better storage quality'] },
                  { name: 'Grapes', emoji: '🍇', ph: '5.5-6.5', link: '/grape', benefits: ['Eliminate chlorosis', 'Boost Brix in arid vineyards', 'Better color development'] },
                  { name: 'Hops', emoji: '🍺', ph: '6.0-6.5', link: '/hops', benefits: ['Improve cone quality and aroma', 'Optimize nutrient flow', 'Better brewing characteristics'] },
                  { name: 'Leeks', emoji: '/Leeks Emoji.png', ph: '6.0-8.0', link: '/leek', benefits: ['Deepen root development', 'Better in tight, high-pH soils', 'Improved shaft quality'] },
                  { name: 'Lettuce', emoji: '🥬', ph: '6.0-7.0', link: '/lettuce', benefits: ['Prevent tip burn', 'Nutrient lockout prevention', 'Better head formation'] },
                  { name: 'Melons', emoji: '/Melon.png', ph: '6.0-7.0', link: '/melon', benefits: ['Boost sugar content', 'Skin firmness improvement', 'Better Ca/Mg balance'] },
                  { name: 'Onions', emoji: '🧅', ph: '6.0-7.0', link: '/onion', benefits: ['Improve sulfur availability', 'Pungency and storage quality', 'Better bulb development'] },

                  // Row 4
                  { name: 'Parsley', emoji: '/Parsley.png', ph: '5.0-7.0', link: '/parsley', benefits: ['Balance pH for lush foliage', 'Ensure aromatic oils', 'Better leaf color'] },
                  { name: 'Peaches', emoji: '🍑', ph: '6.0-7.0', link: '/peach', benefits: ['Avoid zinc and boron deficiency', 'Better fruit development', 'Improved tree vigor'] },
                  { name: 'Pears', emoji: '🍐', ph: '6.0-7.0', link: '/pear', benefits: ['Enhance calcium uptake', 'Reduced internal breakdown', 'Better storage quality'] },
                  { name: 'Peas', emoji: '/Peas Emoji.png', ph: '6.0-7.5', link: '/pea', benefits: ['Support nodulation and growth', 'Better in bicarbonate-prone beds', 'Improved pod fill'] },
                  { name: 'Peppers', emoji: '🌶️', ph: '5.5-7.0', link: '/pepper', benefits: ['Reduce blossom end rot', 'Boost capsaicin production', 'Better fruit set'] },
                  { name: 'Pistachios', emoji: '/Pistachio.png', ph: '6.0-7.5', link: '/pistachio', benefits: ['Correct salt buildup', 'Unlock potassium in desert soils', 'Better nut fill'] },
                  { name: 'Plums', emoji: '🟣', ph: '6.0-7.0', link: '/plum', benefits: ['Strengthen tree vigor', 'Better yield in marginal ground', 'Improved fruit quality'] },
                  { name: 'Potatoes', emoji: '🥔', ph: '4.8-6.5', link: '/potato', benefits: ['Fight scab and boost uniformity', 'Better soil balance', 'Improved tuber quality'] },
                  { name: 'Pumpkins', emoji: '🎃', ph: '5.5-7.5', link: '/pumpkin', benefits: ['Improve stem strength', 'Better skin finish', 'Enhanced storage quality'] },
                  { name: 'Radishes', emoji: '/Radish.png', ph: '6.0-7.0', link: '/radish', benefits: ['Speed maturity', 'Size consistency', 'Optimized uptake'] },

                  // Row 5
                  { name: 'Raspberries', emoji: '/Raspberry Emoji.png', ph: '5.5-6.5', link: '/raspberry', benefits: ['Sweeten berries', 'Prevent cane dieback', 'Better in alkaline zones'] },
                  { name: 'Spinach', emoji: '/Spinach.png', ph: '6.0-7.5', link: '/spinach', benefits: ['Prevent chlorosis', 'Boost leaf turgor', 'Ideal root pH'] },
                  { name: 'Squash', emoji: '/Squash.png', ph: '5.5-7.0', link: '/squash', benefits: ['Avoid uneven fruit development', 'Better in stressed alkaline fields', 'Improved vine health'] },
                  { name: 'Strawberries', emoji: '🍓', ph: '5.5-6.5', link: '/strawberry', benefits: ['Enhance berry size and sugar', 'Better shelf life', 'Improved drip systems'] },
                  { name: 'Sweet Potatoes', emoji: '🍠', ph: '5.8-6.2', link: '/sweet-potato', benefits: ['Improve nutrient density', 'Better skin smoothness', 'Enhanced storage'] },
                  { name: 'Tomatoes', emoji: '🍅', ph: '6.0-6.8', link: '/tomato', benefits: ['Fight blossom end rot', 'Unlock full flavor potential', 'Better fruit set'] },
                  { name: 'Turnips', emoji: '/Turnip Emoji.png', ph: '5.5-6.8', link: '/turnip', benefits: ['Prevent nutrient lockout', 'Avoid bitter roots', 'Better in high-pH soils'] },
                  { name: 'Walnuts', emoji: '/Walnut.png', ph: '6.0-7.5', link: '/walnut', benefits: ['Correct trace mineral imbalance', 'Stronger shell set', 'Better tree health'] },
                  { name: 'Watermelons', emoji: '🍉', ph: '5.8-7.0', link: '/watermelon', benefits: ['Enhance sweetness', 'Better cell structure', 'Improved in hot climates'] },
                  { name: 'Zucchini', emoji: '🥒', ph: '6.0-7.5', link: '/zucchini', benefits: ['Improve flower retention', 'Better fruit shape', 'Reduced irrigation stress'] }
                ].map((crop, index) => {
                  // Check if crop has an existing page
                  const hasExistingPage = ['/almond', '/apple', '/apricot', '/blueberry', '/cannabis', '/cherry', '/grape', '/hops', '/pistachio', '/raspberry', '/tomato'].includes(crop.link);

                  const CropItem = () => (
                    <div className="text-center">
                      <div className="text-xl md:text-2xl mb-1 group-hover:scale-110 transition-transform duration-200">
                        {crop.emoji.startsWith('/') ? (
                          <img
                            src={crop.emoji}
                            alt={crop.name}
                            className="w-5 h-5 md:w-6 md:h-6 mx-auto object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = document.createElement('div');
                              fallback.textContent = '🌱';
                              fallback.className = 'text-xl md:text-2xl';
                              e.currentTarget.parentNode?.appendChild(fallback);
                            }}
                          />
                        ) : (
                          crop.emoji
                        )}
                      </div>
                      <h3 className="text-xs font-bold text-gray-900 mb-1 group-hover:text-green-600 transition-colors leading-tight line-clamp-1">
                        {crop.name}
                      </h3>
                      <div className="text-green-600 font-semibold text-xs mb-1">
                        {crop.ph}
                      </div>
                    </div>
                  );

                  return (
                    <div key={index} className="group cursor-pointer relative">
                      {hasExistingPage ? (
                        <Link
                          to={crop.link}
                          className="block p-1 md:p-2 hover:bg-green-50 rounded transition-all duration-200"
                        >
                          <CropItem />

                          {/* Hover Tooltip */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-56 text-left">
                            <div className="font-semibold mb-1">{crop.name}</div>
                            <div className="text-green-300 mb-2">pH Range: {crop.ph}</div>
                            <ul className="text-gray-300 text-xs space-y-1">
                              {crop.benefits.map((benefit, i) => (
                                <li key={i}>• {benefit}</li>
                              ))}
                            </ul>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </Link>
                      ) : (
                        <div className="block p-1 md:p-2 relative">
                          <CropItem />

                          {/* Hover Tooltip for disabled crops */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-56 text-left">
                            <div className="font-semibold mb-1">{crop.name}</div>
                            <div className="text-green-300 mb-2">pH Range: {crop.ph}</div>
                            <ul className="text-gray-300 text-xs space-y-1">
                              {crop.benefits.map((benefit, i) => (
                                <li key={i}>• {benefit}</li>
                              ))}
                            </ul>
                            <div className="text-yellow-300 mt-1 text-xs">Guide coming soon</div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Water Source Compatibility */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Where to Use Them</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-3">
                  <div className="text-center bg-white rounded-lg p-6 shadow-sm border border-green-200">
                    <div className="text-lg md:text-xl mb-2">🌵</div>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">Arid Regions</h4>
                    <p className="text-gray-600 text-xs leading-tight">High bicarbonate water</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-6 shadow-sm border border-green-200">
                    <div className="text-lg md:text-xl mb-2">🏔️</div>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">Calcareous Soils</h4>
                    <p className="text-gray-600 text-xs leading-tight">Limestone-rich soils</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-6 shadow-sm border border-green-200">
                    <div className="text-lg md:text-xl mb-2">🍇</div>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">Perennial Fruit</h4>
                    <p className="text-gray-600 text-xs leading-tight">Orchards &amp; vineyards</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-6 shadow-sm border border-green-200">
                    <div className="text-lg md:text-xl mb-2">🌾</div>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">Field Crops</h4>
                    <p className="text-gray-600 text-xs leading-tight">Cotton, alfalfa, corn</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-6 shadow-sm border border-green-200">
                    <div className="text-lg md:text-xl mb-2">🍒</div>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">High Value Crops</h4>
                    <p className="text-gray-600 text-xs leading-tight">Cherries, Berries, &amp; Vegetables</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-6 shadow-sm border border-green-200">
                    <div className="text-lg md:text-xl mb-2">⛳</div>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">Turf &amp; Landscape</h4>
                    <p className="text-gray-600 text-xs leading-tight">Golf courses &amp; sports fields</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-6 shadow-sm border border-green-200">
                    <div className="text-lg md:text-xl mb-2">💧</div>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">Hard Water Sources</h4>
                    <p className="text-gray-600 text-xs leading-tight">Canals &amp; surface water</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Sulfur Burners vs Sulfuric Acid Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Sulfur Burners vs. Straight Sulfuric Acid
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Understanding your options for irrigation water acidification
              </p>
            </div>

            <div className="space-y-8">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                Two ways to acidify irrigation water
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Sulfuric Acid Injection Card */}
                <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-1">Sulfuric Acid Injection</h4>
                  <p className="text-sm text-gray-500 mb-6">(delivered acid)</p>

                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-sm font-semibold text-green-700 mb-2">Pros:</p>
                      <ul className="space-y-2">
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                          <span className="text-gray-700 text-sm">Proven chemistry—widely documented in extension guides</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                          <span className="text-gray-700 text-sm">No equipment investment—just injection pump and tank</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                          <span className="text-gray-700 text-sm">Fast pH response—immediate acid availability</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-amber-700 mb-2">Tradeoffs:</p>
                      <ul className="space-y-2">
                        <li className="flex items-start space-x-2">
                          <span className="text-amber-500 text-lg leading-none">⚠</span>
                          <span className="text-gray-700 text-sm">Hazardous material storage and handling</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-amber-500 text-lg leading-none">⚠</span>
                          <span className="text-gray-700 text-sm">Ongoing delivery logistics and costs</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-amber-500 text-lg leading-none">⚠</span>
                          <span className="text-gray-700 text-sm">Corrosion exposure when low-pH water runs daily</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-sm text-gray-700"><strong>Best fit when:</strong> You need flexibility, have small acreage, or prefer outsourcing acid logistics.</p>
                  </div>
                </div>

                {/* Sulfur Burner Card */}
                <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-2xl p-6 border-2 border-ctc-blue shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-1">Sulfur Burner</h4>
                  <p className="text-sm text-gray-500 mb-6">(make acid on-farm)</p>

                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-sm font-semibold text-green-700 mb-2">Pros:</p>
                      <ul className="space-y-2">
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                          <span className="text-gray-700 text-sm">Safe solid feedstock (elemental sulfur pellets)</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                          <span className="text-gray-700 text-sm">Lower operating costs over time</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                          <span className="text-gray-700 text-sm">Reduced delivery frequency and logistics</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-amber-700 mb-2">Tradeoffs:</p>
                      <ul className="space-y-2">
                        <li className="flex items-start space-x-2">
                          <span className="text-amber-500 text-lg leading-none">⚠</span>
                          <span className="text-gray-700 text-sm">Upfront equipment investment</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-amber-500 text-lg leading-none">⚠</span>
                          <span className="text-gray-700 text-sm">Requires commissioning and setup support</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <span className="text-amber-500 text-lg leading-none">⚠</span>
                          <span className="text-gray-700 text-sm">Maintenance and monitoring needed</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-ctc-blue/20 rounded-lg p-3 border border-ctc-blue">
                    <p className="text-sm text-gray-700"><strong>Best fit when:</strong> You want long-term pH control, safety, and predictable operating costs at scale.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-6 border border-gray-200 text-center">
                <p className="text-gray-700 leading-relaxed">
                  Both can work when designed and managed correctly. The right choice depends on safety, logistics, corrosion exposure, and how often you're correcting pH/alkalinity.
                </p>
              </div>

              {/* Cost Calculator Callout */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calculator className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Want to see the cost difference in dollars?
                    </h3>
                    <p className="text-lg text-gray-600 mb-6">
                      Run a quick comparison between your sulfuric acid spend and elemental sulfur through a sulfur burner using your own rough numbers.
                    </p>
                    <button
                      onClick={() => setIsCalculatorModalOpen(true)}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg inline-flex items-center space-x-2"
                    >
                      <Calculator size={20} />
                      <span>Run Cost Comparison</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Choose CTC - CONSOLIDATED */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Why Choose CTC
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Blue-collar equipment. White-glove standards. Built for predictable operation—without heroics.
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200 mb-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-ctc-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">Engineered build quality (not a farm-shop fab)</p>
                    <p className="text-gray-600 text-sm">Precision stainless fabrication, repeatable fit-up, and consistent combustion control.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-ctc-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">Materials that hold up in acidified water environments</p>
                    <p className="text-gray-600 text-sm">Acid-resistant alloys where it matters.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-ctc-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">Designed for maintainability</p>
                    <p className="text-gray-600 text-sm">Accessible service points, predictable wear components, and straightforward upkeep.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-ctc-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">Documentation + setup support</p>
                    <p className="text-gray-600 text-sm">Commissioning checklist, operating targets, and support that doesn't disappear after shipment.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-ctc-blue rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">Decades of field proof across irrigation water conditions</p>
                    <p className="text-gray-600 text-sm">pH correction, bicarbonates, and system protection.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTC vs The Other Guys Comparison Table */}
            <div className="bg-gradient-to-br from-ctc-silver-light to-white rounded-2xl p-6 border border-ctc-silver shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                CTC vs The Other Guys
              </h3>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ctc-silver">
                      <th className="text-left py-3 px-4 text-gray-700 font-medium">Feature</th>
                      <th className="text-center py-3 px-4 text-ctc-blue font-bold">CTC</th>
                      <th className="text-center py-3 px-4 text-gray-500 font-medium">The Other Guys</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-ctc-silver/30 hover:bg-ctc-blue/5 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">Materials</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <CheckCircle className="text-green-500" size={18} />
                          <span className="text-gray-700 text-xs">Acid-resistant alloys</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-gray-400 text-lg">○</span>
                          <span className="text-gray-600 text-xs">Standard materials</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-ctc-silver/30 hover:bg-ctc-blue/5 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">Build Quality</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <CheckCircle className="text-green-500" size={18} />
                          <span className="text-gray-700 text-xs">Precision manufacturing</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-gray-400 text-lg">○</span>
                          <span className="text-gray-600 text-xs">Variable</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-ctc-silver/30 hover:bg-ctc-blue/5 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">Automation</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <CheckCircle className="text-green-500" size={18} />
                          <span className="text-gray-700 text-xs">Automated controls</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-gray-400 text-lg">○</span>
                          <span className="text-gray-600 text-xs">May require manual operation</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-ctc-silver/30 hover:bg-ctc-blue/5 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">Components</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <CheckCircle className="text-green-500" size={18} />
                          <span className="text-gray-700 text-xs">Premium grade parts</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-gray-400 text-lg">○</span>
                          <span className="text-gray-600 text-xs">Basic components</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-ctc-blue/5 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">Support</td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <CheckCircle className="text-green-500" size={18} />
                          <span className="text-gray-700 text-xs">Dedicated expert service</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-gray-400 text-lg">○</span>
                          <span className="text-gray-600 text-xs">Limited support</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Start Simple Block - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="bg-gradient-to-br from-cyan-50 via-blue-50 to-teal-50 rounded-2xl p-8 border-2 border-cyan-300 shadow-lg">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Start Simple. Get More Precise When You Can.</h2>
                <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                  Most projects start with pH and flow. When customers share additional water or soil data, we can tailor operating targets and commissioning steps for more confidence.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-6 rounded-xl font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-lg flex flex-col items-center space-y-3"
                >
                  <Zap size={32} />
                  <div>
                    <div className="text-xl mb-1">Quick Quote (pH + GPM)</div>
                    <div className="text-sm text-cyan-100">Fast Lane: Get started with basics</div>
                  </div>
                </button>

                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-6 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg flex flex-col items-center space-y-3"
                >
                  <FileText size={32} />
                  <div>
                    <div className="text-xl mb-1">Tailored Plan (Upload Records)</div>
                    <div className="text-sm text-blue-100">Engineered Lane: More data, tighter expectations</div>
                  </div>
                </button>
              </div>
            </div>
          </motion.div>

          {/* FAQ Section - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Answers to common questions about salinity, sodicity, and treated irrigation
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="space-y-4">
                {faqData.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <HelpCircle className="text-ctc-blue flex-shrink-0" size={20} />
                        <span className="text-left font-semibold text-gray-900">{faq.question}</span>
                      </div>
                      <div className={`transform transition-transform ${openFAQ === index ? 'rotate-180' : ''}`}>
                        <ArrowRight className="text-gray-400" size={20} />
                      </div>
                    </button>
                    {openFAQ === index && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Learn More Links - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Learn More</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/sulfur-burner-guide"
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-200 hover:border-blue-400"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Sulfur Burner Guide</h4>
                  <p className="text-gray-600 text-sm mb-4">Comprehensive guide to pH management and sustainable agriculture</p>
                  <div className="flex items-center text-ctc-blue font-medium">
                    <span>Read guide</span>
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                </Link>

                <Link
                  to="/research"
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-200 hover:border-blue-400"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Research & University Projects</h4>
                  <p className="text-gray-600 text-sm mb-4">See how our systems perform in university research trials</p>
                  <div className="flex items-center text-ctc-blue font-medium">
                    <span>View research</span>
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                </Link>

                <Link
                  to="/blog/texas-a-m-agrilife-cotton-study-sulfur-burner-treated-irrigation-reduced-root-zone-salinity-and-increased-yield-20"
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-200 hover:border-blue-400"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Texas A&M Article</h4>
                  <p className="text-gray-600 text-sm mb-4">Cotton study results: salinity reduction and yield increase</p>
                  <div className="flex items-center text-ctc-blue font-medium">
                    <span>Read article</span>
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Getting Started Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Getting Started is Easy
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From water analysis to system installation, we guide you through every step
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                {
                  step: '1',
                  title: 'Record Review',
                  description: 'We review your existing water/soil reports (or help you source a basic test if you want one)'
                },
                {
                  step: '2',
                  title: 'System Design',
                  description: 'We size the system for your flow and target outcomes'
                },
                {
                  step: '3',
                  title: 'Install + Startup',
                  description: 'Simple install. We provide a checklist and walk you through startup/commissioning'
                },
                {
                  step: '4',
                  title: 'Support',
                  description: 'Ongoing support, parts, and operating guidance'
                }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl font-bold">{item.step}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center bg-gradient-to-r from-ctc-blue via-ctc-blue-dark to-black rounded-2xl p-12 text-white shadow-2xl"
          >
            <h3 className="text-3xl font-bold mb-4">
              <span className="block">Better Water = Better Crops:</span>
              <span className="block">Stop pH Problems from Stealing Your Profits</span>
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Join the generations of farmers who have trusted CTC to fix their pH problems
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => setIsContactModalOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-ctc-silver-light transition-colors inline-flex items-center justify-center space-x-2 shadow-lg"
              >
                <span>Get Custom Quote</span>
                <ArrowRight size={20} />
              </motion.button>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/sulfur-burner-guide"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-ctc-blue transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <span>Better Water Guide</span>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Unified Contact Modal */}
      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="sulfur-burners"
        formSource="product_quote"
      />

      {/* Newsletter Modal */}
      {isNewsletterModalOpen && (
        <NewsletterPopup />
      )}

      {/* Calculator Modal */}
      <AnimatePresence>
        {isCalculatorModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => setIsCalculatorModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl my-8"
            >
              <button
                onClick={() => setIsCalculatorModalOpen(false)}
                className="absolute -top-4 -right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
              >
                <X size={24} className="text-gray-600" />
              </button>
              <AcidCostCalculator sourcePage="sulfur-burners-modal" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default SulfurBurnersPage;
